import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AccountService } from '../../store/services/account.service';
import { SessionService } from '../../store/services/session.service';
import { IIntegrationData, IUserSessionData } from '../../store/models/user.model';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ContactsService } from '../../store/services/contacts.service';
import { IContactGroup } from '../../store/models/contact.model';
import { ISelectItem } from '../../store/models/select.model';
import { Button, ButtonColor, ButtonSize } from '../../store/models/button.model';
import { Store } from '@ngrx/store';
import { IReducers } from '../../store/reducers';
import { openSnackbar } from '../../store/actions/page.action';
import { ButtonDisablerService } from '../../store/services/button-disabler.service';

const NOT_SHOWN_FIELDS: string[] = ['id', 'account_id'];

@Component({
  selector: 'app-integrations',
  templateUrl: './integrations.component.html',
  styleUrls: ['./integrations.component.scss'],
})
export class IntegrationsComponent implements OnInit {

  integrationId: number;
  userSession: IUserSessionData;
  integrationData: IIntegrationData;
  formGroup: FormGroup;

  componentReady = false;
  userGroups: IContactGroup[] = [];

  submitButtonProperties: Button = {
    text: 'Save',
    color: ButtonColor.GREEN,
    size: ButtonSize.NORMAL,
    onClick: () => this.submit(),
  };

  private sourceControlActive: {
    [key: string]: boolean;
  } = {};

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly accountsService: AccountService,
    private readonly sessionService: SessionService,
    private readonly contactsService: ContactsService,
    private readonly store$: Store<IReducers>,
    private readonly buttonDisabler: ButtonDisablerService
  ) {
    this.activatedRoute.params.subscribe(params => {
      this.integrationId = params.id;

      this.sessionService.onReady().subscribe(userSession => {
        this.userSession = userSession;
        this.loadData();
      });
    });
  }

  get fields(): string[] {
    return Object.keys(this.integrationData.fields).filter(f => NOT_SHOWN_FIELDS.indexOf(f) === -1);
  }

  loadData() {
    this.componentReady = false;
    this.accountsService.getIntegrationMap(this.integrationId)
      .subscribe(result => {
        this.integrationData = result;
        this.formGroup = new FormGroup({});
        this.formGroup.addControl('account_id', new FormControl(this.userSession.id));
        Object.keys(this.integrationData.fields).filter(f => NOT_SHOWN_FIELDS.indexOf(f) === -1).forEach(field => {
          this.formGroup.addControl(
            field,
            new FormControl(
              this.integrationData.map[field] || '',
              this.integrationData.fields[field].required ? [Validators.required] : []),
          );

          if (this.integrationData.fields[field].control === 'list') {
            if (
              this.integrationData.fields[field].control_source === 'groups' &&
              !this.sourceControlActive[this.integrationData.fields[field].control_source]
            ) {
              this.contactsService.getContactGroups(this.userSession.id, {
                except: 'internal',
              })
                .subscribe(groups => this.userGroups = groups);
              this.sourceControlActive[this.integrationData.fields[field].control_source] = true;
            }
          }
        });
        this.componentReady = true;
      });
  }

  ngOnInit(): void {

  }

  getDynamicList(type: string | object, valueType: string): ISelectItem[] {
    if (typeof type === 'object') {
      return Object.keys(type).map(field => ({
        label: type[field],
        value: valueType === 'integer' ? +field : field,
      }));
    }
    if (type === 'groups') {
      return this.userGroups.map(g => ({
        label: g.group_name,
        value: g.group_id,
      }));
    }

    return [];
  }

  submit() {
    if (!this.formGroup.valid) {
      this.formGroup.markAllAsTouched();
      this.buttonDisabler.disableActive();
      return;
    }

    let action = 'updateIntegration';
    if (!this.userSession.integration.map) {
      action = 'createIntegration';
    }

    this.accountsService[action](this.integrationId, this.formGroup.value, this.integrationData.map.id).subscribe(result => {
      this.store$.dispatch(openSnackbar({
        snackbarData: {
          data: {
            message: `${this.userSession.integration.name} Integration was successfully ${!!this.userSession.integration.map ? 'updated' : 'created'}`,
            type: 'success',
          },
        },
      }));
      this.loadData();
    }, err => this.store$.dispatch(openSnackbar({
      snackbarData: {
        data: {
          message: err.error,
          type: 'error',
        },
      },
    })));
  }

}
